import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { acceptNsuOfferAPI, verifyOtp } from "../_request";
import { useAuth } from "context";
import OtpConfirmationModal from "examples/Modal/OtpConfirmationModal";
import CircularProgress from "@mui/material/CircularProgress";
import logo from "assets/images/icons/catering-icons/cr-logo-black.png";
import "../../style.css";

const SUPPORT_EMAIL = "support@cateringrewards.com";
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=Catering%20Rewards%20%E2%80%94%20Support%20request`;
const RETRY_DEGRADE_WINDOW_MS = 30_000;

const REWARD_STATUS = { CREDITED: "credited", PENDING: "pending" };

const PRIMARY_BTN_SX = {
  backgroundColor: "#238856",
  "&:hover": { backgroundColor: "#1e7049" },
  borderRadius: "8px",
  padding: "12px 32px",
  fontSize: "16px",
  fontWeight: "bold",
  width: { xs: "100%", sm: "auto" },
};

const OUTLINE_BTN_SX = {
  borderRadius: "8px",
  padding: "12px 32px",
  fontSize: "16px",
  fontWeight: "bold",
  width: { xs: "100%", sm: "auto" },
};

const Shell = ({ children, onContactSupport }) => (
  <MDBox
    sx={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #F4FAF6 0%, #FFFFFF 60%)",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <MDBox
      component="header"
      sx={{
        height: 72,
        bgcolor: "#fff",
        borderBottom: "1px solid #E5E7EB",
        display: "flex",
        alignItems: "center",
        px: { xs: 3, md: 6 },
      }}
    >
      <img src={logo} alt="Catering Rewards" style={{ height: 36 }} />
    </MDBox>
    <MDBox
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 6,
      }}
    >
      <MDBox
        sx={{
          width: "100%",
          maxWidth: 620,
          bgcolor: "#fff",
          border: "1px solid #E5E7EB",
          borderTop: "4px solid #238856",
          borderRadius: "16px",
          boxShadow: "0 4px 24px rgba(16, 24, 40, 0.06)",
          padding: { xs: "32px 24px", md: "48px 40px" },
          textAlign: "center",
        }}
      >
        {children}
      </MDBox>
    </MDBox>
    <MDBox
      component="footer"
      sx={{
        py: 3,
        textAlign: "center",
        color: "#667085",
        fontSize: 13,
      }}
    >
      © Catering Rewards ·{" "}
      <a href={SUPPORT_MAILTO} onClick={onContactSupport} style={{ color: "#667085" }}>
        {SUPPORT_EMAIL}
      </a>
    </MDBox>
  </MDBox>
);

Shell.propTypes = {
  children: PropTypes.node.isRequired,
  onContactSupport: PropTypes.func.isRequired,
};

const resolveView = (status, body) => {
  const message = body.message || "";
  const data = body.data || {};

  if (status === 200 && body.success && data.alreadyCustomer) {
    return { view: "alreadyCustomer" };
  }
  // Credited, but no auto-login — recipient must enter the emailed OTP first (200 and 202).
  if ((status === 200 || status === 202) && body.success && data.otpRequired) {
    return { view: "otp" };
  }
  if (status === 410) {
    return { view: "expired" };
  }
  if (status === 409) {
    if (/expired/i.test(message)) return { view: "expired" };
    if (/already.*accepted|no longer acceptable/i.test(message)) return { view: "alreadyClaimed" };
    return { view: "serverError" };
  }
  if (status === 400 || status === 404) {
    return { view: "invalidLink", statusCode: status };
  }
  if (status >= 500) {
    return { view: "serverError" };
  }
  return { view: "serverError" };
};

function ClaimReward() {
  const [token] = useState(() => new URL(window.location.href).searchParams.get("token") || null);
  const { saveAuthData, showSnackbarWithMessage } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [expiredData, setExpiredData] = useState(null);
  const [otpEmail, setOtpEmail] = useState(null);
  const [rewardStatus, setRewardStatus] = useState(null);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const lastRetryAt = useRef(0);
  const [retryDegraded, setRetryDegraded] = useState(false);
  // Guards against duplicate POSTs when the claim effect re-fires (auth-context update, React 18 StrictMode).
  const claimFiredRef = useRef(false);

  const claim = useCallback(async () => {
    if (claimFiredRef.current) return;
    claimFiredRef.current = true;

    if (!token) {
      setLoading(false);
      setView("invalidLink");
      return;
    }

    try {
      const response = await acceptNsuOfferAPI(token);
      const status = response.status;
      const body = response.data || {};

      const resolved = resolveView(status, body);
      setStatusCode(resolved.statusCode || status || null);
      if (resolved.view === "expired" && body.data) {
        setExpiredData(body.data);
      }
      if (resolved.view === "otp" && body.data) {
        setOtpEmail(body.data.email || null);
        setRewardStatus(
          body.data.credited === true ? REWARD_STATUS.CREDITED : REWARD_STATUS.PENDING
        );
      }
      setView(resolved.view);
    } catch (err) {
      setView("serverError");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    claim();
  }, [claim]);

  // Gate the "Sign in instead" link for 30s so the emailed code has time to arrive.
  useEffect(() => {
    if (view !== "otp") return undefined;
    setResendCountdown(30);
    const id = setInterval(() => {
      setResendCountdown((s) => {
        if (s <= 1) {
          clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [view]);

  const handleOtpVerify = async (otpCode) => {
    const { data: body } = await verifyOtp({ identifier: otpEmail, otp: otpCode });
    if (!body || !body.success) {
      throw new Error((body && body.message) || "That code didn't work. Please try again.");
    }
    const tokens = body.data && body.data.tokens;
    if (!tokens) {
      throw new Error("We couldn't sign you in. Please try again, or contact support.");
    }
    await saveAuthData(tokens);
    setView("success");
  };

  const handleRetry = async () => {
    const now = Date.now();
    // Within the degrade window → 2nd retry on a server error swaps the primary CTA to "Email support".
    if (lastRetryAt.current && now - lastRetryAt.current < RETRY_DEGRADE_WINDOW_MS) {
      setRetryDegraded(true);
      return;
    }
    lastRetryAt.current = now;
    setRetrying(true);
    claimFiredRef.current = false;
    await claim();
    setRetrying(false);
  };

  const contactSupport = () => {
    try {
      navigator.clipboard?.writeText(SUPPORT_EMAIL);
    } catch (e) {
      // clipboard unavailable
    }
    showSnackbarWithMessage({ type: "success", message: "Support email copied to clipboard." }, 4000);
  };

  return (
    <Shell onContactSupport={contactSupport}>
      {(loading || !view) && (
        <MDBox sx={{ textAlign: "center" }}>
          <CircularProgress color="success" />
          <MDTypography variant="body1" color="text" mt={2}>
            Activating your reward — this takes just a moment.
          </MDTypography>
        </MDBox>
      )}

      {!loading && view === "success" && (
        <>
          <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
            {rewardStatus === REWARD_STATUS.PENDING
              ? "You're all set — your reward is on its way"
              : "You're all set — your reward is ready"}
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={3}>
            {rewardStatus === REWARD_STATUS.PENDING
              ? "We're finalizing your reward; it'll appear in your account shortly. Head to your dashboard to keep an eye on it."
              : "Your welcome reward points are now in your account. Head to your dashboard to see your balance."}
          </MDTypography>
          <MDButton
            variant="gradient"
            color="success"
            size="large"
            onClick={() => navigate("/dashboard")}
            sx={PRIMARY_BTN_SX}
          >
            Go to my dashboard
          </MDButton>
        </>
      )}

      {!loading && view === "alreadyCustomer" && (
        <>
          <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
            Welcome back — you&apos;re already with us
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={3}>
            We recognized your email — your existing Catering Rewards account is now linked to this
            restaurant too. Welcome rewards are for first-time accounts, but you&apos;ll keep
            earning on every order from here.
          </MDTypography>
          <MDButton
            component={Link}
            to="/authentication/sign-in"
            variant="gradient"
            color="success"
            size="large"
            sx={PRIMARY_BTN_SX}
          >
            Sign in
          </MDButton>
        </>
      )}

      {!loading && view === "otp" && (
        <>
          <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
            Verify it&apos;s you
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={2}>
            {rewardStatus === REWARD_STATUS.PENDING
              ? "Your reward is on its way."
              : "Your reward points are already in your account."}{" "}
            To sign in, enter the 6-digit code we just emailed to <strong>{otpEmail}</strong>.
          </MDTypography>
          <OtpConfirmationModal
            open
            identifier={otpEmail}
            type="email"
            onVerify={handleOtpVerify}
            resendSlot={
              <MDBox sx={{ ml: "2px" }}>
                <MDTypography color="textSecondary" sx={{ fontSize: "14px" }} fontWeight="normal">
                  Can&apos;t find the code? Check your spam or junk folder.
                </MDTypography>
                <MDTypography
                  color="textSecondary"
                  sx={{ fontSize: "14px", mt: 1 }}
                  fontWeight="normal"
                >
                  Still no code?{" "}
                  {resendCountdown > 0 ? (
                    <span style={{ color: "#98A2B3" }}>
                      sign-in available in {resendCountdown}s
                    </span>
                  ) : (
                    <MDTypography
                      component="button"
                      type="button"
                      onClick={() => setView("deferred")}
                      variant="button"
                      color="success"
                      sx={{
                        background: "none",
                        border: "none",
                        p: 0,
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      Sign in instead
                    </MDTypography>
                  )}
                </MDTypography>
              </MDBox>
            }
          />
        </>
      )}

      {!loading && view === "deferred" && (
        <>
          <MDTypography variant="h4" fontWeight="bold" color="text" mb={3}>
            {rewardStatus === REWARD_STATUS.PENDING
              ? "You're all set — your reward is on its way"
              : "You're all set — your reward is ready"}
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={3} sx={{ lineHeight: 1.7 }}>
            {rewardStatus === REWARD_STATUS.PENDING ? (
              <>
                We&apos;re finalizing your reward; it&apos;ll land in your account shortly. Sign in
                with <strong>{otpEmail}</strong> to keep an eye on it.
              </>
            ) : (
              <>
                Your welcome reward points are already in your account. Sign in with{" "}
                <strong>{otpEmail}</strong> to view your balance.
              </>
            )}
          </MDTypography>
          <MDButton
            component={Link}
            to="/authentication/sign-in"
            variant="gradient"
            color="success"
            size="large"
            sx={PRIMARY_BTN_SX}
          >
            Sign in
          </MDButton>
        </>
      )}

      {!loading && view === "alreadyClaimed" && (
        <>
          <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
            You&apos;ve already claimed this one
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={3}>
            Looks like you accepted this reward already — it&apos;s sitting in your account, ready
            to redeem. Sign in to take a look.
          </MDTypography>
          <MDButton
            component={Link}
            to="/authentication/sign-in"
            variant="gradient"
            color="success"
            size="large"
            sx={PRIMARY_BTN_SX}
          >
            Sign in to your account
          </MDButton>
          <MDBox mt={2}>
            <MDTypography
              component="a"
              href={SUPPORT_MAILTO}
              onClick={contactSupport}
              variant="button"
              color="text"
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Contact support
            </MDTypography>
          </MDBox>
        </>
      )}

      {!loading && view === "expired" && expiredData?.alreadyCustomer && (
        <>
          <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
            Welcome back — this link has run out
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={3}>
            The reward link has expired, but you&apos;re already with us. Sign in to check your
            points balance and keep earning on every order.
          </MDTypography>
          <MDButton
            component={Link}
            to="/authentication/sign-in"
            variant="gradient"
            color="success"
            size="large"
            sx={PRIMARY_BTN_SX}
          >
            Sign in
          </MDButton>
          <MDBox mt={2}>
            <MDTypography
              component="a"
              href={SUPPORT_MAILTO}
              onClick={contactSupport}
              variant="button"
              color="text"
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Contact support
            </MDTypography>
          </MDBox>
        </>
      )}

      {!loading && view === "expired" && !expiredData?.alreadyCustomer && (
        <>
          <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
            This offer has expired
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={2}>
            {expiredData?.highestCashbackReward
              ? `The reward link has expired, but you can still earn up to ${expiredData.highestCashbackReward}X (~${expiredData.highestCashbackReward}%) back on every order — sign up below and start earning today.`
              : "The reward link has expired. Reach out to the restaurant or our support team — we can see about getting you a new one."}
          </MDTypography>
          {expiredData?.restaurantOnboardingLink ? (
            <>
              <MDButton
                href={expiredData.restaurantOnboardingLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="gradient"
                color="success"
                size="large"
                sx={PRIMARY_BTN_SX}
              >
                Sign up to earn rewards
              </MDButton>
              <MDBox mt={2}>
                <MDTypography
                  component="a"
                  href={SUPPORT_MAILTO}
                  onClick={contactSupport}
                  variant="button"
                  color="text"
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  Contact support
                </MDTypography>
              </MDBox>
            </>
          ) : (
            <>
              <MDButton
                href={SUPPORT_MAILTO}
                onClick={contactSupport}
                variant="outlined"
                color="dark"
                size="large"
                sx={OUTLINE_BTN_SX}
              >
                Contact support
              </MDButton>
              <MDBox mt={2}>
                <MDTypography
                  component={Link}
                  to="/authentication/sign-in"
                  variant="button"
                  color="text"
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  Sign in
                </MDTypography>
              </MDBox>
            </>
          )}
        </>
      )}

      {!loading && view === "invalidLink" && (
        <>
          <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
            {statusCode === 404 ? "We couldn't find this offer" : "We couldn't read this link"}
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={3}>
            {statusCode === 404
              ? "The order this reward is tied to is no longer in our records. The restaurant will need to send you a new link."
              : "Something looks off — the link might've gotten cut short when it was copied. Reach out to the restaurant and they can send you a fresh one."}
          </MDTypography>
          <MDButton
            href={SUPPORT_MAILTO}
            onClick={contactSupport}
            variant="outlined"
            color="dark"
            size="large"
            sx={OUTLINE_BTN_SX}
          >
            Contact support
          </MDButton>
        </>
      )}

      {!loading && view === "serverError" && (
        <>
          <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
            Something went wrong on our end
          </MDTypography>
          <MDTypography variant="body1" color="text" mb={3}>
            Your reward is safe and your link still works. Give it another try in a moment — and if
            the trouble keeps up, we&apos;re a click away.
          </MDTypography>
          {retryDegraded ? (
            <MDBox>
              <MDTypography variant="body2" color="text" mb={2}>
                Still not working — please reach out and we&apos;ll sort it out for you.
              </MDTypography>
              <MDButton
                href={SUPPORT_MAILTO}
                onClick={contactSupport}
                variant="gradient"
                color="success"
                size="large"
                sx={PRIMARY_BTN_SX}
              >
                Email support
              </MDButton>
            </MDBox>
          ) : (
            <MDBox>
              <MDButton
                onClick={handleRetry}
                disabled={retrying}
                variant="gradient"
                color="success"
                size="large"
                sx={PRIMARY_BTN_SX}
              >
                {retrying ? "Trying again…" : "Try again"}
              </MDButton>
              <MDBox mt={2}>
                <MDTypography
                  component="a"
                  href={SUPPORT_MAILTO}
                  onClick={contactSupport}
                  variant="button"
                  color="text"
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  Contact support
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </>
      )}
    </Shell>
  );
}

export default ClaimReward;
